import { useCallback, useContext } from 'react';
import SlideSetsContext from '../toc/_contexts/SlideSetsContext';
import useAnalytics from '../../../hooks/useAnalytics';
import type { AnalyticsEvent } from '../../../hooks/useAnalytics';
import { ANALYTICS_EVENT_TYPES } from '../../../constants/analyticsEvents';
import { deleteUserSlideSet } from '../../../api/slide-set';
import type { SlideSet } from '../../../api/slide-set';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';

const DEFAULT_PROVIDER = 'pelckmans';
const BASE_URL = process.env.REACT_APP_DOCEO_TEMPLATE_URL;

export default function useSlideSets() {
  const { slideSets, setSlideSets, fetchSlideSets, isLoading } = useContext(SlideSetsContext);
  const { capture } = useAnalytics();
  const handleError = useThrowAsyncError();

  const buildDoceoUrl = (
    slideSet: { id: number; versionId?: number; state?: string },
    isEditMode?: boolean,
    eventSubLocation?: string,
  ) => {
    const provider = localStorage.getItem('p-provider') || DEFAULT_PROVIDER;

    const query = new URLSearchParams();
    if (isEditMode) query.set('edit', isEditMode.toString());
    if (eventSubLocation) query.set('origin', eventSubLocation);
    if (slideSet.state !== undefined && slideSet.state !== 'PUBLISHED' && slideSet.versionId)
      query.set('versionId', slideSet.versionId.toString());

    const url = isEditMode === undefined ? BASE_URL : `${BASE_URL}/overview`;
    return `${url.replace(':provider', provider).replace(':id', slideSet.id.toString())}?${query}`;
  };

  const captureOpenSlideSetEvents = useCallback(
    (slideSetId: number, subLocationId: AnalyticsEvent['subLocationId']) => {
      capture({
        objectId: slideSetId.toString(),
        eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_ACCESSED,
        subLocationId,
      });

      capture({
        objectId: slideSetId.toString(),
        eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_VIEWER_ACCESSED,
        subLocationId,
      });
    },
    [capture],
  );

  const buildCopySlideSetUrl = (
    moduleId: string,
    nodeId: string,
    slideSetId: number,
    versionId: number,
    eventSubLocation: string,
  ) => {
    const provider = localStorage.getItem('p-provider') || DEFAULT_PROVIDER;

    const query = new URLSearchParams({
      moduleId,
      nodeId,
      versionId: versionId.toString(),
      origin: eventSubLocation,
    });

    return `${BASE_URL.replace(':provider', provider).replace(':id', slideSetId.toString())}/copy?${query}`;
  };

  const buildNewSlideSetUrl = (moduleId: string, nodeId: string, eventSubLocation: string) => {
    const provider = localStorage.getItem('p-provider') || DEFAULT_PROVIDER;

    const query = new URLSearchParams({
      moduleId,
      nodeId,
      origin: eventSubLocation,
    });

    return `${BASE_URL.replace(':provider', provider).replace(':id', 'new')}?${query}`;
  };

  const deleteSlideSet = async (slideSet: SlideSet) => {
    try {
      await deleteUserSlideSet(slideSet);
    } catch (e) {
      handleError(e);
    }
  };

  return {
    slideSets,
    fetchSlideSets,
    buildDoceoUrl,
    setSlideSets,
    isLoading,
    captureOpenSlideSetEvents,
    buildCopySlideSetUrl,
    buildNewSlideSetUrl,
    deleteUserSlideSet: deleteSlideSet,
  };
}
